import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { BACKEND_URL, whatsappHref, apiErrorMsg } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Wallet, Star, Users, Phone, MessageCircle, Building2, Sparkles, Send, Plus, X, Upload, Download, ArrowRightLeft, AlertTriangle, Search, Filter, Cake, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { initials } from "@/lib/api";
import CeapIA from "@/components/CeapIA";
import { NovoOrgaoModal } from "@/components/NovoOrgaoModal";

const CATEGORIAS_LABEL = {
  cliente: "Clientes",
  consumidor: "Consumidores",
  cliente_potencial: "Cliente Potencial",
  deixou_de_vir: "Deixaram de vir",
  nao_cliente: "Não clientes",
  blacklist: "Blacklist",
};

const CATEGORIAS_COR = {
  cliente: "border-emerald-300 bg-emerald-50",
  consumidor: "border-amber-300 bg-amber-50",
  cliente_potencial: "border-blue-300 bg-blue-50",
  deixou_de_vir: "border-amber-300 bg-amber-50",
  nao_cliente: "border-slate-300 bg-slate-50",
  blacklist: "border-red-300 bg-red-50",
};

const CATEGORIAS_ICON = {
  cliente: "🎯",
  consumidor: "🌱",
  cliente_potencial: "🌱",
  deixou_de_vir: "🥲",
  nao_cliente: "💤",
  blacklist: "🚫",
};

const COR_PROSPEC = {
  verde: "border-emerald-500 bg-emerald-50",
  amarelo: "border-amber-500 bg-amber-50",
  vermelho: "border-red-500 bg-red-50",
  branco: "border-slate-200 bg-white",
};

/* =====================================================
   Selo Blacklist — botão + popup de motivo (item 1)
   ===================================================== */
function BlacklistBadge({ pessoa, onChanged }) {
  const { user } = useAuth();
  const [dlg, setDlg] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);
  const isBL = !!pessoa.blacklist;
  const podeRemover = user?.role === "diretoria";

  const abrir = () => {
    if (isBL && !podeRemover) {
      toast.error("Apenas Diretoria pode remover Blacklist manualmente.");
      return;
    }
    setMotivo(pessoa.motivo_blacklist || "");
    setDlg(true);
  };

  const confirmar = async () => {
    setBusy(true);
    try {
      if (isBL) {
        await api.patch(`/pessoas/${pessoa.id}/blacklist`, { blacklist: false, motivo: "" });
        toast.success("Removido da Blacklist");
      } else {
        if (!motivo.trim()) { toast.error("Descreva o motivo"); setBusy(false); return; }
        await api.patch(`/pessoas/${pessoa.id}/blacklist`, { blacklist: true, motivo: motivo.trim() });
        toast.success("Marcado como Blacklist");
      }
      setDlg(false);
      onChanged?.();
    } catch (e) {
      toast.error(apiErrorMsg(e, "Erro"));
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={abrir}
        className={`inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-widest px-2 py-1 rounded-md border-2 transition ${isBL ? "bg-red-600 border-red-700 text-white shadow-md hover:bg-red-700" : "bg-white border-red-400 text-red-600 hover:bg-red-50"}`}
        data-testid={`btn-blacklist-${pessoa.id}`}
        title={isBL ? (pessoa.motivo_blacklist || "Cliente na concorrência") : "Marcar como Blacklist"}
      >
        🚫 Blacklist
      </button>
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent data-testid="dialog-blacklist">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">🚫</span>
              {isBL ? "Remover da Blacklist" : "Marcar como Blacklist"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-slate-600">
              <b>{pessoa.nome}</b> — {pessoa.funcao}
            </p>
            {isBL ? (
              <p className="text-sm text-slate-600">
                Motivo atual: <i>{pessoa.motivo_blacklist || "—"}</i>. Confirme para remover.
              </p>
            ) : (
              <>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Por que Blacklist? *</label>
                <Textarea
                  rows={3}
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Ex.: cliente atual da concorrência X, contrato exclusivo, indisponibilidade orçamentária..."
                  data-testid="input-motivo-blacklist"
                />
                <p className="text-[11px] text-slate-500">
                  A pessoa sai da Blacklist automaticamente quando efetivar uma inscrição em curso.
                </p>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)} disabled={busy}>Cancelar</Button>
            <Button
              onClick={confirmar}
              disabled={busy}
              className={isBL ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}
              data-testid="btn-confirmar-blacklist"
            >
              {busy ? "Salvando..." : (isBL ? "Confirmar remoção" : "Marcar Blacklist")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* =====================================================
   Card compacto de município (usado nas seções)
   ===================================================== */
function MunicipioCard({ m, prioridade, ordem, onOpen }) {
  const corClass = m.prospec_cor ? COR_PROSPEC[m.prospec_cor] : "border-slate-200 bg-white";
  // Iter 46: lead MKT ativo = borda rosa vibrante + halo pulsante
  const isLeadMkt = m.lead_mkt_pendente_ativo;
  const cardCls = isLeadMkt
    ? "border-fuchsia-500 bg-fuchsia-50 ring-2 ring-fuchsia-300/60 shadow-lg shadow-fuchsia-200/50"
    : corClass;
  const ordemStyle = prioridade
    ? "text-amber-700 font-bold"
    : "text-slate-400 font-semibold";
  return (
    <button
      onClick={() => onOpen(m)}
      className={`text-left rounded-xl border-2 hover:shadow-md transition p-3 w-full ${cardCls}`}
      data-testid={`carteira-mun-${m.id}`}
    >
      {isLeadMkt && (
        <div className="mb-1.5 inline-flex items-center gap-1 bg-fuchsia-600 text-white rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest" data-testid={`chip-lead-mkt-topo-${m.id}`}>
          🎯 Lead MKT — atender primeiro
        </div>
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-slate-900 truncate flex items-center gap-1.5">
            {ordem != null && (
              <span
                className={`text-[11px] tabular-nums flex-shrink-0 ${ordemStyle}`}
                title="Prioridade automática nesta coluna"
                data-testid={`prio-ordem-${m.id}`}
              >
                #{ordem}
              </span>
            )}
            {prioridade && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-400 flex-shrink-0" />}
            <span className="truncate">{m.nome}</span>
          </div>
          <div className="flex flex-wrap items-center gap-1 mt-1 text-xs">
            <span className="inline-flex items-center gap-1 bg-slate-100 rounded px-1.5 py-0.5 text-slate-700 font-medium">
              {m.uf}
            </span>
            <span className="inline-flex items-center gap-1 bg-blue-50 rounded px-1.5 py-0.5 text-blue-700 font-medium">
              {m.tipo_orgao || "Câmara"}
            </span>
            {m.inscricoes_12m > 0 && (
              <span className="inline-flex items-center gap-1 bg-emerald-50 rounded px-1.5 py-0.5 text-emerald-700 font-medium" title="Inscrições nos últimos 12 meses">
                {m.inscricoes_12m}× 12m
              </span>
            )}
            {typeof m.avg_estrelas === "number" && m.avg_estrelas > 0 && (
              <span className="inline-flex items-center gap-1 bg-yellow-50 rounded px-1.5 py-0.5 text-yellow-700 font-medium" title="Média de receptividade">
                ★ {m.avg_estrelas.toFixed(1)}
              </span>
            )}
            {m.qtd_blacklist > 0 && (
              <span className="inline-flex items-center gap-1 bg-red-100 rounded px-1.5 py-0.5 text-red-700 font-bold" title="Pessoas em Blacklist">
                🚫 {m.qtd_blacklist}
              </span>
            )}
            {typeof m.prospec_qtd === "number" && m.prospec_qtd > 0 && (
              <span className="inline-flex items-center gap-1 bg-white/70 rounded px-1.5 py-0.5 text-slate-700 font-bold" title="Pessoas prospectadas no mês">
                🎯 {m.prospec_qtd}
              </span>
            )}
            {m.leads_mkt_qtd > 0 && (
              <span
                className="inline-flex items-center gap-1 bg-fuchsia-100 rounded px-1.5 py-0.5 text-fuchsia-800 font-bold animate-pulse"
                title={`${m.leads_mkt_qtd} lead${m.leads_mkt_qtd > 1 ? 's' : ''} de campanha MKT ativo${m.leads_mkt_qtd > 1 ? 's' : ''} neste município`}
                data-testid={`badge-lead-mkt-${m.id}`}
              >
                🎯 LEAD MKT{m.leads_mkt_qtd > 1 ? ` ×${m.leads_mkt_qtd}` : ""}
              </span>
            )}
            {m.categoria === "nao_cliente" && m.inteligencia_prioridade?.hist_24m_qtd > 0 && (
              <span
                className="inline-flex items-center gap-1 bg-purple-100 rounded px-1.5 py-0.5 text-purple-800 font-bold"
                title={`Já comprou ${m.inteligencia_prioridade.hist_24m_qtd}× em ${(m.inteligencia_prioridade.hist_24m_anos || []).join(", ")}`}
                data-testid={`badge-historico-${m.id}`}
              >
                🎯 {m.inteligencia_prioridade.hist_24m_qtd}× {(m.inteligencia_prioridade.hist_24m_anos || []).join("-")}
              </span>
            )}
            {m.categoria === "nao_cliente" && (m.inteligencia_prioridade?.razoes || []).length > 0 && (
              <span
                className="inline-flex items-center gap-1 bg-indigo-100 rounded px-1.5 py-0.5 text-indigo-800 font-bold cursor-help"
                title={"Inteligência de Prioridade:\n• " + m.inteligencia_prioridade.razoes.join("\n• ")}
                data-testid={`badge-inteligencia-${m.id}`}
              >
                💡 {m.inteligencia_prioridade.score}
              </span>
            )}
          </div>
          {(m.funis_futuros || []).length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {m.funis_futuros.map((fm) => (
                <span key={fm} className="text-[10px] font-bold bg-indigo-100 text-indigo-700 rounded px-1.5 py-0.5" data-testid={`tarja-futuro-${m.id}-${fm}`}>
                  → funil {fm}
                </span>
              ))}
            </div>
          )}
          <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
            <Users className="w-3 h-3" /> {m.qtd_pessoas} pessoa{m.qtd_pessoas !== 1 && "s"}
            {m.ponto_focal_nome && (
              <span className="ml-1 truncate">
                · <b className="text-amber-700">PF:</b> {m.ponto_focal_nome}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

/* =====================================================
   Modal Detalhe do Município — registrar contato,
   ver histórico, editar pessoas, enviar para curso
   ===================================================== */
function ModalDetalheMunicipio({ open, onClose, municipioId, onChanged }) {
  const { user } = useAuth();
  const [detail, setDetail] = useState(null);
  const [novaPessoaOpen, setNovaPessoaOpen] = useState(false);
  const [nova, setNova] = useState({ nome: "", funcao: "Vereador", telefone: "", email: "", data_nascimento: "" });
  const [obs, setObs] = useState({}); // {pessoa_id: obs}
  const [enviarOpen, setEnviarOpen] = useState(false);

  const load = useCallback(() => {
    if (!municipioId) return;
    Promise.all([
      api.get(`/municipios/${municipioId}`).catch(() => ({ data: null })),
      api.get(`/pessoas?municipio_id=${municipioId}`).catch(() => ({ data: [] })),
      api.get(`/contatos?municipio_id=${municipioId}`).catch(() => ({ data: [] })),
    ]).then(([mR, pR, cR]) => {
      if (!mR.data) return;
      setDetail({ municipio: mR.data, pessoas: pR.data || [], contatos: (cR.data || []).slice(0, 50) });
    });
  }, [municipioId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open || !detail) return null;
  const { municipio: m, pessoas: pessoasRaw = [], contatos = [] } = detail;

  // Ordena: clientes → consumidores → não clientes (Iter 31). Institucionais/blacklist ao final.
  const _catRankP = (p) => {
    if (p.blacklist) return 90;
    if (p.institucional) return 80;
    const c = p.categoria || (p.frequencia >= 2 ? "cliente" : p.frequencia === 1 ? "consumidor" : "nao_cliente");
    if (c === "cliente") return 0;
    if (c === "consumidor") return 1;
    if (c === "nao_cliente") return 2;
    return 50;
  };
  const pessoas = [...pessoasRaw].sort((a, b) => {
    const ra = _catRankP(a);
    const rb = _catRankP(b);
    if (ra !== rb) return ra - rb;
    if (a.ponto_focal && !b.ponto_focal) return -1;
    if (!a.ponto_focal && b.ponto_focal) return 1;
    return (a.nome || "").localeCompare(b.nome || "");
  });

  const registrarContato = async (p, canal) => {
    const observ = (obs[p.id] || "").trim();
    if (!observ) {
      toast.error("Descreva o que foi conversado antes de registrar");
      return;
    }
    try {
      await api.post("/contatos", {
        pessoa_id: p.id,
        municipio_id: m.id,
        canal,
        observacao: observ,
      });
      toast.success(`Contato registrado (${canal === "ligacao" ? "Ligação" : "WhatsApp"})`);
      setObs((o) => ({ ...o, [p.id]: "" }));
      load();
      onChanged?.();
    } catch { toast.error("Erro ao registrar contato"); }
  };

  const criarPessoa = async () => {
    if (!nova.nome.trim()) { toast.error("Nome obrigatório"); return; }
    try {
      await api.post("/pessoas", {
        nome: nova.nome.trim(),
        funcao: nova.funcao,
        telefone: nova.telefone || null,
        email: nova.email || null,
        data_nascimento: nova.data_nascimento || null,
        municipio_id: m.id,
      });
      toast.success("Pessoa cadastrada");
      setNova({ nome: "", funcao: "Vereador", telefone: "", email: "", data_nascimento: "" });
      setNovaPessoaOpen(false);
      load();
    } catch { toast.error("Erro ao cadastrar pessoa"); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="modal-mun-detalhe">
          <DialogHeader className="pb-3 border-b">
            <DialogTitle className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                <span className="font-display text-2xl">{m.nome}</span>
              </div>
              <div className="flex flex-wrap items-center gap-1 text-xs">
                <span className="bg-slate-100 rounded px-2 py-0.5 text-slate-700 font-medium">{m.uf}</span>
                <span className="bg-blue-50 rounded px-2 py-0.5 text-blue-700 font-medium">{m.tipo_orgao}</span>
                <span className={`rounded px-2 py-0.5 font-medium ${CATEGORIAS_COR[m.categoria || "nao_cliente"]}`}>
                  {CATEGORIAS_ICON[m.categoria || "nao_cliente"]} {CATEGORIAS_LABEL[m.categoria || "nao_cliente"]}
                </span>
                {(m.frequencia_12m || 0) > 0 && (
                  <span className="bg-amber-50 rounded px-2 py-0.5 text-amber-700 font-medium">
                    {m.frequencia_12m}× nos últimos 12 meses
                  </span>
                )}
              </div>
              {m.telefone && (
                <div className="text-xs font-normal">
                  <a
                    href={whatsappHref(m.telefone)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-emerald-700 hover:underline inline-flex items-center gap-1 font-medium"
                    data-testid="modal-mun-telefone-wa"
                    title="Abrir no WhatsApp Web"
                  >
                    <MessageCircle className="w-3 h-3" /> {m.telefone}
                  </a>
                </div>
              )}
              {m.email_institucional && <div className="text-xs text-slate-500 font-normal">✉️ {m.email_institucional}</div>}
              {m.ponto_focal_nome && <div className="text-xs text-slate-500 font-normal">👤 Ponto focal: {m.ponto_focal_nome}</div>}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-auto py-3 space-y-4">
            {/* Pessoas */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <Users className="w-4 h-4" /> Pessoas ({pessoas.length})
                </div>
                <Button size="sm" variant="outline" onClick={() => setNovaPessoaOpen(true)} data-testid="btn-nova-pessoa">
                  <Plus className="w-3 h-3 mr-1" /> Nova pessoa
                </Button>
              </div>
              {pessoas.length === 0 ? (
                <div className="text-sm text-slate-400 py-3 italic">Nenhuma pessoa cadastrada — clique em "Nova pessoa" para começar.</div>
              ) : (
                <div className="space-y-2">
                  {pessoas.map((p) => {
                    const temObs = (obs[p.id] || "").trim().length > 0;
                    return (
                    <div key={p.id} className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`pessoa-linha-${p.id}`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-slate-900">
                            {p.nome}
                            {p.frequencia > 0 && <span className="ml-2 text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded font-medium">{p.frequencia}× em cursos</span>}
                          </div>
                          <div className="text-xs text-slate-500 flex flex-wrap items-center gap-x-1">
                            <span>{p.funcao}</span>
                            {p.telefone && (
                              <>
                                <span>·</span>
                                <a
                                  href={whatsappHref(p.telefone)}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-emerald-700 hover:underline font-medium inline-flex items-center gap-0.5"
                                  onClick={(e) => e.stopPropagation()}
                                  data-testid={`carteira-pessoa-tel-wa-${p.id}`}
                                  title="Abrir no WhatsApp Web"
                                >
                                  <MessageCircle className="w-3 h-3" /> {p.telefone}
                                </a>
                              </>
                            )}
                            {p.data_nascimento && <> · 🎂 {p.data_nascimento.slice(5)}</>}
                          </div>
                        </div>
                        <div className="flex gap-1 flex-wrap">
                          <BlacklistBadge pessoa={p} onChanged={load} />
                          <Button size="sm" variant="outline" disabled={!temObs} onClick={() => registrarContato(p, "ligacao")} data-testid={`btn-lig-${p.id}`} className="text-xs">
                            <Phone className="w-3 h-3 mr-1" /> Ligação
                          </Button>
                          <Button size="sm" variant="outline" disabled={!temObs} onClick={() => registrarContato(p, "whatsapp")} data-testid={`btn-zap-${p.id}`} className="text-xs">
                            <MessageCircle className="w-3 h-3 mr-1" /> Whats
                          </Button>
                        </div>
                      </div>
                      <Input
                        placeholder="Descreva o que foi conversado (obrigatório)*"
                        value={obs[p.id] || ""}
                        onChange={(e) => setObs((o) => ({ ...o, [p.id]: e.target.value }))}
                        className={`text-sm mt-2 ${temObs ? "" : "border-amber-300 focus:border-amber-500"}`}
                        data-testid={`obs-pessoa-${p.id}`}
                      />
                      {!temObs && (
                        <div className="text-[11px] text-amber-600 mt-1">💬 Preencha a conversa para habilitar Ligação/Whats.</div>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Histórico */}
            {contatos.length > 0 && (
              <div>
                <div className="text-sm font-bold text-slate-800 flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4" /> Histórico ({contatos.length})
                </div>
                <div className="space-y-1 max-h-56 overflow-auto">
                  {contatos.slice(0, 30).map((c) => (
                    <div key={c.id} className="text-xs bg-slate-50 rounded px-2 py-1.5 flex items-start gap-2">
                      <span className={`inline-block px-1.5 rounded ${c.canal === "ligacao" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>
                        {c.canal === "ligacao" ? "LIG" : "ZAP"}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-slate-700">
                          <b>{c.pessoa_nome || "—"}</b> · {c.vendedora_nome} · {new Date(c.data).toLocaleString("pt-BR")}
                        </div>
                        {c.observacao && <div className="text-slate-500 italic">"{c.observacao}"</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-3">
            <Button variant="outline" onClick={onClose}>Fechar</Button>
            <Button
              onClick={() => setEnviarOpen(true)}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={pessoas.length === 0}
              data-testid="btn-enviar-curso"
            >
              <Send className="w-4 h-4 mr-1" /> Enviar prospecção para curso
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sub-modal: nova pessoa */}
      <Dialog open={novaPessoaOpen} onOpenChange={setNovaPessoaOpen}>
        <DialogContent data-testid="modal-nova-pessoa">
          <DialogHeader><DialogTitle>Nova pessoa em {m.nome}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Input placeholder="Nome*" value={nova.nome} onChange={(e) => setNova({ ...nova, nome: e.target.value })} data-testid="input-nova-nome" />
            <Select value={nova.funcao} onValueChange={(v) => setNova({ ...nova, funcao: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Vereador", "Presidente", "Assessor", "Secretário", "Contador", "Servidor", "Outro"].map((f) => (
                  <SelectItem key={f} value={f}>{f}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input placeholder="Telefone/WhatsApp" value={nova.telefone} onChange={(e) => setNova({ ...nova, telefone: e.target.value })} />
            <Input placeholder="E-mail" value={nova.email} onChange={(e) => setNova({ ...nova, email: e.target.value })} />
            <Input type="date" placeholder="Data de nascimento" value={nova.data_nascimento} onChange={(e) => setNova({ ...nova, data_nascimento: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNovaPessoaOpen(false)}>Cancelar</Button>
            <Button onClick={criarPessoa} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-salvar-nova-pessoa">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sub-modal: enviar prospecção */}
      {enviarOpen && (
        <ModalEnviarProspeccao
          open={enviarOpen}
          onClose={() => setEnviarOpen(false)}
          municipio={m}
          pessoas={pessoas}
          onDone={() => { setEnviarOpen(false); toast.success("Pessoas enviadas para o funil! 🚀"); onChanged?.(); }}
        />
      )}
    </>
  );
}

/* =====================================================
   Modal: Enviar prospecção para curso
   ===================================================== */
const UFS_BR_CART = [
  "AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI",
  "RJ","RN","RS","RO","RR","SC","SP","SE","TO",
];
const MESES_BR_CART = [
  { v: "01", l: "Jan" }, { v: "02", l: "Fev" }, { v: "03", l: "Mar" }, { v: "04", l: "Abr" },
  { v: "05", l: "Mai" }, { v: "06", l: "Jun" }, { v: "07", l: "Jul" }, { v: "08", l: "Ago" },
  { v: "09", l: "Set" }, { v: "10", l: "Out" }, { v: "11", l: "Nov" }, { v: "12", l: "Dez" },
];

function ModalEnviarProspeccao({ open, onClose, municipio, pessoas, onDone }) {
  const [cursos, setCursos] = useState([]);
  const [cursoId, setCursoId] = useState("");
  const [atrib, setAtrib] = useState({}); // {pessoa_id: estagio}
  // Iter 57: filtros UF/Ano/Mês antes do curso
  const [filtroUf, setFiltroUf] = useState("");
  const [filtroAno, setFiltroAno] = useState("");
  const [filtroMes, setFiltroMes] = useState("");
  // Iter 42: motivo de perda quando algum pessoa vai para "não quer"
  const [motivos, setMotivos] = useState({});
  const [motivoOpen, setMotivoOpen] = useState(false);
  const [motivoCat, setMotivoCat] = useState("");
  const [motivoSub, setMotivoSub] = useState("");
  const [motivoObs, setMotivoObs] = useState("");

  useEffect(() => {
    if (!open) return;
    api.get("/cursos?status=ativo").then((r) => setCursos(r.data || [])).catch(() => setCursos([]));
    api.get("/motivos-perda").then((r) => setMotivos(r.data || {})).catch(() => setMotivos({}));
    setMotivoCat(""); setMotivoSub(""); setMotivoObs("");
    // Default: UF do município + mês atual
    setFiltroUf((municipio?.uf || "").toUpperCase());
    const now = new Date();
    setFiltroAno(String(now.getFullYear()));
    setFiltroMes(String(now.getMonth() + 1).padStart(2, "0"));
    setCursoId("");
  }, [open, municipio?.uf]);

  const anosDisponiveis = useMemo(() => {
    const set = new Set();
    (cursos || []).forEach((c) => c.mes_ano && set.add((c.mes_ano || "").split("-")[0]));
    const arr = [...set].sort().reverse();
    return arr.length ? arr : [String(new Date().getFullYear())];
  }, [cursos]);

  const cursosFiltrados = useMemo(() => {
    return (cursos || []).filter((c) => {
      // Cursos sem UF são "nacionais" — passam por qualquer filtro de UF
      if (filtroUf && c.uf && (c.uf || "").toUpperCase() !== filtroUf) return false;
      if (filtroAno && !((c.mes_ano || "").startsWith(filtroAno))) return false;
      if (filtroMes) {
        const parts = (c.mes_ano || "").split("-");
        if (parts[1] !== filtroMes) return false;
      }
      return true;
    }).sort((a, b) => (b.mes_ano || "").localeCompare(a.mes_ano || ""));
  }, [cursos, filtroUf, filtroAno, filtroMes]);

  useEffect(() => {
    if (cursoId && !cursosFiltrados.some((c) => c.id === cursoId)) setCursoId("");
  }, [cursosFiltrados, cursoId]);

  const limparFiltros = () => { setFiltroUf(""); setFiltroAno(""); setFiltroMes(""); };

  const naoQuerCount = Object.values(atrib).filter((v) => v === "nao_querem").length;

  const doEnviar = async (motivoPayload) => {
    const atribuicoes = Object.entries(atrib)
      .filter(([, e]) => !!e)
      .map(([pessoa_id, estagio]) => ({ pessoa_id, estagio }));
    try {
      const body = { municipio_id: municipio.id, curso_id: cursoId, atribuicoes };
      if (motivoPayload) body.motivo_perda = motivoPayload;
      await api.post("/prospeccao/enviar-curso", body);
      toast.success("Enviado ao funil ✓");
      onDone?.();
    } catch (e) { toast.error(apiErrorMsg(e, "Erro ao enviar")); }
  };

  const enviar = async () => {
    if (!cursoId) { toast.error("Escolha um curso"); return; }
    const atribuicoes = Object.entries(atrib).filter(([, e]) => !!e);
    if (atribuicoes.length === 0) { toast.error("Escolha o estágio de pelo menos uma pessoa"); return; }
    // Iter 42: se há "não quer", exige motivo antes
    if (naoQuerCount > 0) {
      setMotivoOpen(true);
      return;
    }
    await doEnviar(null);
  };

  const confirmarMotivoEEnviar = async () => {
    if (!motivoCat || !motivoSub) { toast.error("Selecione categoria e motivo"); return; }
    setMotivoOpen(false);
    await doEnviar({ categoria: motivoCat, sub: motivoSub, obs: motivoObs });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="modal-enviar-curso">
        <DialogHeader>
          <DialogTitle>Enviar {municipio.nome} para um curso</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {/* Iter 57: filtros UF/Ano/Mês */}
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-600">Filtrar cursos por</label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              <Select value={filtroUf} onValueChange={(v) => setFiltroUf(v === "__all__" ? "" : v)}>
                <SelectTrigger data-testid="prospeccao-filtro-uf"><SelectValue placeholder="Estado" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__all__">Todos</SelectItem>
                  {UFS_BR_CART.map((u) => (<SelectItem key={u} value={u}>{u}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={filtroAno} onValueChange={(v) => setFiltroAno(v === "__all__" ? "" : v)}>
                <SelectTrigger data-testid="prospeccao-filtro-ano"><SelectValue placeholder="Ano" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos</SelectItem>
                  {anosDisponiveis.map((a) => (<SelectItem key={a} value={a}>{a}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={filtroMes} onValueChange={(v) => setFiltroMes(v === "__all__" ? "" : v)}>
                <SelectTrigger data-testid="prospeccao-filtro-mes"><SelectValue placeholder="Mês" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos</SelectItem>
                  {MESES_BR_CART.map((m) => (<SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            {(filtroUf || filtroAno || filtroMes) && (
              <button
                type="button"
                onClick={limparFiltros}
                className="mt-1 text-[11px] text-slate-500 hover:text-slate-800 font-bold"
                data-testid="prospeccao-filtros-limpar"
              >Limpar filtros</button>
            )}
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-600">
              Curso {cursosFiltrados.length > 0 && (<span className="text-slate-400 font-normal normal-case tracking-normal">({cursosFiltrados.length} disponíve{cursosFiltrados.length === 1 ? "l" : "is"})</span>)}
            </label>
            <Select value={cursoId} onValueChange={setCursoId}>
              <SelectTrigger data-testid="select-curso-envio"><SelectValue placeholder="Escolha um curso..." /></SelectTrigger>
              <SelectContent className="max-h-72">
                {cursosFiltrados.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-slate-400">Nenhum curso bate com esses filtros.</div>
                ) : cursosFiltrados.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.uf ? `${c.uf} · ` : "🇧🇷 · "}{c.nome} · {c.mes_ano}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-600">Distribua as pessoas nas colunas do funil</label>
            <div className="text-[11px] text-slate-500 mb-2">
              Para inscrever oficialmente, use o menu <b>Registrar Inscrição</b> no funil.
            </div>
            <div className="space-y-2 mt-2 max-h-80 overflow-auto">
              {pessoas.map((p) => (
                <div key={p.id} className="flex items-center gap-3 border border-slate-200 rounded-lg p-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{p.nome}</div>
                    <div className="text-xs text-slate-500">{p.funcao}</div>
                  </div>
                  <Select value={atrib[p.id] || ""} onValueChange={(v) => setAtrib((a) => ({ ...a, [p.id]: v }))}>
                    <SelectTrigger className="w-48" data-testid={`select-estagio-${p.id}`}>
                      <SelectValue placeholder="— ignorar —" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="incerto">Incerto</SelectItem>
                      <SelectItem value="interessado">Interessado</SelectItem>
                      <SelectItem value="certo">Certo</SelectItem>
                      <SelectItem value="nao_querem">Não quer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
          {naoQuerCount > 0 && (
            <div className="text-[11px] bg-amber-50 border border-amber-200 text-amber-800 rounded p-2">
              ⚠️ {naoQuerCount} pessoa(s) marcada(s) como "não quer" — será solicitado o <b>motivo de perda</b> ao enviar.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={enviar} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-enviar-curso-conf">
            <Send className="w-4 h-4 mr-1" /> Enviar
          </Button>
        </DialogFooter>

        {/* Iter 42: modal secundária de motivo de perda */}
        <Dialog open={motivoOpen} onOpenChange={(o) => !o && setMotivoOpen(false)}>
          <DialogContent data-testid="motivo-perda-envio-dialog">
            <DialogHeader><DialogTitle>Por que essas pessoas não vão comprar?</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-600">Categoria *</label>
                <Select value={motivoCat} onValueChange={(v) => { setMotivoCat(v); setMotivoSub(""); }}>
                  <SelectTrigger data-testid="motivo-envio-cat"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(motivos).map((k) => (<SelectItem key={k} value={k}>{k}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              {motivoCat && (
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-600">Motivo específico *</label>
                  <Select value={motivoSub} onValueChange={setMotivoSub}>
                    <SelectTrigger data-testid="motivo-envio-sub"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      {(motivos[motivoCat] || []).map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-600">Observação (opcional)</label>
                <Input value={motivoObs} onChange={(e) => setMotivoObs(e.target.value)} data-testid="motivo-envio-obs" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMotivoOpen(false)}>Cancelar</Button>
              <Button onClick={confirmarMotivoEEnviar} className="bg-red-600 hover:bg-red-700" data-testid="motivo-envio-salvar">
                Registrar e Enviar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

/* =====================================================
   Vendedora: 5 seções
   ===================================================== */
function CarteiraVendedora({ vendedoraIdOverride = null, headerExtra = null }) {
  const nav = useNavigate();
  const [novoOrgaoOpen, setNovoOrgaoOpen] = useState(false);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [filtroCor, setFiltroCor] = useState(""); // "verde"|"amarelo"|"vermelho"|"branco"|""
  const [filtroBlacklist, setFiltroBlacklist] = useState(false); // toggle "ver só municípios c/ pessoas Blacklist"

  const load = useCallback(() => {
    const url = vendedoraIdOverride
      ? `/carteira?vendedora_id=${vendedoraIdOverride}`
      : "/carteira";
    api.get(url).then((r) => setData(r.data)).catch(() => {});
  }, [vendedoraIdOverride]);
  useEffect(() => { load(); }, [load]);

  const novoProcesso = async () => {
    if (!window.confirm("Iniciar novo processo comercial? As cores serão zeradas (as tarjas de funis futuros são mantidas).")) return;
    try {
      await api.post("/carteira/novo-processo-comercial");
      toast.success("Novo processo iniciado. Cores zeradas.");
      load();
    } catch { toast.error("Erro"); }
  };

  if (!data) return <div className="text-slate-500">Carregando carteira...</div>;

  const filtrar = (arr) => {
    let out = arr || [];
    const s = search.trim().toLowerCase();
    if (s) out = out.filter((m) => m.nome.toLowerCase().includes(s) || m.uf.toLowerCase().includes(s));
    if (filtroCor && data.show_cores) out = out.filter((m) => (m.prospec_cor || "branco") === filtroCor);
    if (filtroBlacklist) out = out.filter((m) => (m.qtd_blacklist || 0) > 0);
    return out;
  };

  const colunas = [
    { key: "prioridades", label: "Prioridades", items: data.prioridades || [], head: "border-amber-400 bg-amber-50", isPrio: true },
    { key: "clientes", label: "Clientes", items: data.clientes || [], head: "border-emerald-400 bg-emerald-50", cat: "cliente" },
    { key: "consumidores", label: "Consumidores", items: data.consumidores || [], head: "border-amber-400 bg-amber-50", cat: "consumidor" },
    { key: "nao_clientes", label: "Não Clientes", items: data.nao_clientes || [], head: "border-red-400 bg-red-50", cat: "nao_cliente" },
  ];

  return (
    <div className="space-y-4" data-testid="carteira-vendedora">
      {headerExtra}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <Input
            placeholder="Buscar município..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="input-buscar-carteira"
          />
        </div>
        {data.show_cores && (
          <div className="flex items-center gap-1" data-testid="filtro-cor">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-widest mr-1">Cor:</span>
            {[
              { k: "", label: "Todas", cor: "bg-slate-200 text-slate-700" },
              { k: "verde", label: "Verde ≥4", cor: "bg-emerald-500 text-white" },
              { k: "amarelo", label: "Amarelo 2-3", cor: "bg-amber-500 text-white" },
              { k: "vermelho", label: "Vermelho 1", cor: "bg-red-500 text-white" },
              { k: "branco", label: "Branco 0", cor: "bg-white border border-slate-300 text-slate-700" },
            ].map((c) => (
              <button
                key={c.k || "todas"}
                onClick={() => setFiltroCor(c.k)}
                className={`text-xs px-2 py-1 rounded font-bold ${c.cor} ${filtroCor === c.k ? "ring-2 ring-blue-500 ring-offset-1" : ""}`}
                data-testid={`btn-cor-${c.k || "todas"}`}
              >{c.label}</button>
            ))}
          </div>
        )}
        <Button
          onClick={() => setFiltroBlacklist((v) => !v)}
          variant={filtroBlacklist ? "default" : "outline"}
          className={filtroBlacklist
            ? "bg-red-600 hover:bg-red-700 text-white font-black"
            : "border-red-400 text-red-700 hover:bg-red-50 font-bold"}
          data-testid="btn-filtro-blacklist"
          title={filtroBlacklist ? "Mostrando só municípios com Blacklist" : "Ver só municípios com pessoas em Blacklist"}
        >
          🚫 Blacklist
          {(data.blacklist || []).length > 0 && (
            <span className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-white/25 tabular-nums">
              {(data.blacklist || []).reduce((s, m) => s + (m.qtd_pessoas || 0), 0)}
            </span>
          )}
        </Button>
        <Button
          onClick={novoProcesso}
          variant="outline"
          className="border-blue-300 text-blue-700 hover:bg-blue-50"
          data-testid="btn-novo-processo"
        >
          <Sparkles className="w-4 h-4 mr-1" /> Novo processo comercial
        </Button>
        <Button onClick={() => setNovoOrgaoOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-novo-orgao">
          <Plus className="w-4 h-4 mr-1" /> Novo órgão
        </Button>
        <NovoOrgaoModal open={novoOrgaoOpen} onClose={() => setNovoOrgaoOpen(false)}
          onCreated={(m) => { setNovoOrgaoOpen(false); nav(`/municipio/${m.id}`); }} />
      </div>

      {filtroBlacklist && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800 flex items-center justify-between gap-3" data-testid="banner-filtro-blacklist">
          <div>
            <b>Modo Blacklist ativo</b> — exibindo apenas municípios com pessoas em Blacklist. Ideal para força-tarefa de resgate.
          </div>
          <button
            type="button"
            onClick={() => setFiltroBlacklist(false)}
            className="text-red-700 font-bold hover:underline text-xs"
            data-testid="btn-limpar-filtro-blacklist"
          >Limpar filtro</button>
        </div>
      )}

      {data.show_cores && (
        <div className="text-xs text-slate-500 italic">
          As cores indicam quantas pessoas você prospectou em cada município neste mês (≥4 verde · 2-3 amarelo · 1 vermelho · 0 branco).
        </div>
      )}

      {/* Funil horizontal: 4 colunas (Blacklist agora é filtro, não coluna) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="carteira-funil">
        {colunas.map((col) => {
          const items = filtrar(col.items);
          const totalPessoas = items.reduce((s, m) => s + (m.qtd_pessoas || 0), 0);
          return (
            <div key={col.key} className={`rounded-2xl border-2 ${col.head}`} data-testid={`col-${col.key}`}>
              <div className="p-3 border-b border-black/5">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs uppercase font-bold tracking-widest text-slate-700 truncate">{col.label}</div>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-white text-slate-700 tabular-nums" data-testid={`col-total-${col.key}`}>
                    {items.length}
                  </span>
                </div>
                {col.cat && totalPessoas > 0 && (
                  <div className="mt-1 text-[10px] text-slate-500 font-semibold" data-testid={`col-pessoas-${col.key}`}>
                    {totalPessoas} pessoa{totalPessoas !== 1 ? "s" : ""}
                  </div>
                )}
              </div>
              <div className="p-2 space-y-2 max-h-[70vh] overflow-auto">
                {items.length === 0 && (
                  <div className="text-slate-400 text-xs italic p-2">—</div>
                )}
                {items.map((m, idx) => (
                  <MunicipioCard
                    key={`${m.id}-${idx}`}
                    m={m}
                    prioridade={col.isPrio}
                    ordem={m.ordem ?? idx + 1}
                    onOpen={(mun) => {
                      const q = col.cat ? `?cat=${col.cat}` : "";
                      nav(`/municipio/${mun.id}${q}`);
                    }}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* =====================================================
   Diretoria/Supervisão: gestão + importação + prioridades
   ===================================================== */
function CarteiraDiretoria() {
  const nav = useNavigate();
  const [novoOrgaoOpen, setNovoOrgaoOpen] = useState(false);
  const [muns, setMuns] = useState([]);
  const [users, setUsers] = useState([]);
  const [prioridades, setPrioridades] = useState([]);
  const [filtro, setFiltro] = useState({ search: "", vendedora_id: "", categoria: "", uf: "" });
  const [visao, setVisao] = useState("tabela"); // "tabela" | "colunas_geral" | "colunas_vendedora"
  const [selected, setSelected] = useState(new Set());
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadVendedora, setUploadVendedora] = useState("");
  const [prioMesOpen, setPrioMesOpen] = useState(false);
  // Iter 67 · Diretor abre a barra de prioridades só se clicar; MASTER vê aberto por padrão
  const [prioridadesExpanded, setPrioridadesExpanded] = useState(false);
  const [zerarOpen, setZerarOpen] = useState(false);
  const { user } = useAuth();

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (filtro.search) params.set("search", filtro.search);
    if (filtro.vendedora_id) params.set("vendedora_id", filtro.vendedora_id);
    if (filtro.categoria) params.set("categoria", filtro.categoria);
    if (filtro.uf) params.set("uf", filtro.uf);
    api.get(`/carteira/todos?${params}`).then((r) => setMuns(r.data || []));
    api.get("/users").then((r) => setUsers(r.data || []));
    api.get("/prioridades").then((r) => setPrioridades(r.data || []));
  }, [filtro]);

  useEffect(() => { load(); }, [load]);

  const vendedoras = users.filter((u) => u.role === "vendedora");
  const prioIds = new Set(prioridades.map((p) => p.municipio_id));

  const toggleSelect = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };
  const allSelected = muns.length > 0 && selected.size === muns.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(muns.map((m) => m.id)));

  const transferir = async (destinoId) => {
    if (selected.size === 0) { toast.error("Selecione municípios"); return; }
    const destinoNome = destinoId
      ? (users.find((u) => u.id === destinoId)?.nome || "vendedora selecionada")
      : "nenhuma vendedora (remover atribuição)";
    const qtd = selected.size;
    if (!window.confirm(
      `Tem certeza da troca de carteira?\n\n` +
      `${qtd} município${qtd > 1 ? "s" : ""} ${qtd > 1 ? "serão transferidos" : "será transferido"} para:\n` +
      `→ ${destinoNome}\n\n` +
      `Essa ação afeta imediatamente o funil e prioridades da vendedora.`
    )) return;
    try {
      await api.post("/carteira/transferir", {
        municipio_ids: Array.from(selected),
        vendedora_destino_id: destinoId || null,
      });
      toast.success(`✅ ${qtd} município${qtd > 1 ? "s transferidos" : " transferido"} para ${destinoNome}`);
      setSelected(new Set());
      load();
    } catch { toast.error("Erro"); }
  };

  const mudarCategoria = async (cat) => {
    if (selected.size === 0) { toast.error("Selecione municípios"); return; }
    try {
      await api.post("/carteira/mudar-categoria", {
        municipio_ids: Array.from(selected),
        categoria: cat,
      });
      toast.success("Categoria atualizada");
      setSelected(new Set());
      load();
    } catch { toast.error("Erro"); }
  };

  const removerPrioridade = async (pid) => {
    try { await api.delete(`/prioridades/${pid}`); load(); toast.success("Removida"); } catch { toast.error("Erro"); }
  };

  const baixarTemplate = async () => {
    try {
      const r = await api.get("/carteira/template-download", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "template_carteira_ceap.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Erro ao baixar template"); }
  };

  const importar = async () => {
    if (!uploadFile) { toast.error("Escolha um arquivo"); return; }
    const fd = new FormData();
    fd.append("file", uploadFile);
    let url = "/carteira/importar-excel";
    if (uploadVendedora) url += `?vendedora_id=${uploadVendedora}`;
    try {
      const r = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Municípios: ${r.data.municipios.criados} criados / ${r.data.municipios.atualizados} atualizados · Pessoas: ${r.data.pessoas.criados} criadas / ${r.data.pessoas.atualizados} atualizadas`);
      setUploadOpen(false); setUploadFile(null); setUploadVendedora("");
      load();
    } catch (e) { toast.error(apiErrorMsg(e, "Erro na importação")); }
  };

  const zerarFunis = async () => {
    try {
      const r = await api.post("/admin/zerar-funis");
      toast.success(`${r.data.cards_removidos} cards removidos`);
      setZerarOpen(false);
    } catch { toast.error("Erro"); }
  };

  return (
    <div className="space-y-4" data-testid="carteira-diretoria">
      {/* Toggle de visualização: Tabela geral × Colunas geral × Por vendedora (colunas) */}
      <Card className="p-3 rounded-2xl bg-gradient-to-r from-slate-50 to-blue-50 border-blue-200">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs uppercase font-bold tracking-widest text-slate-600">Visualização:</span>
          <Button
            size="sm"
            variant={visao === "tabela" ? "default" : "outline"}
            onClick={() => { setVisao("tabela"); setFiltro({ ...filtro, vendedora_id: "" }); }}
            className={visao === "tabela" ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}
            data-testid="btn-visao-tabela"
          >
            📋 Tabela geral (todas)
          </Button>
          <Button
            size="sm"
            variant={visao === "colunas_geral" ? "default" : "outline"}
            onClick={() => { setVisao("colunas_geral"); setFiltro({ ...filtro, vendedora_id: "" }); }}
            className={visao === "colunas_geral" ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}
            data-testid="btn-visao-colunas-geral"
          >
            🗂️ Colunas geral (todas)
          </Button>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant={visao === "colunas_vendedora" ? "default" : "outline"}
              onClick={() => setVisao("colunas_vendedora")}
              className={visao === "colunas_vendedora" ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}
              data-testid="btn-visao-colunas-vendedora"
            >
              👤 Colunas por vendedora
            </Button>
            {visao === "colunas_vendedora" && (
              <Select
                value={filtro.vendedora_id || "__none__"}
                onValueChange={(v) => setFiltro({ ...filtro, vendedora_id: v === "__none__" ? "" : v })}
              >
                <SelectTrigger className="w-64 bg-white" data-testid="select-visao-vendedora">
                  <SelectValue placeholder="Selecionar vendedora →" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Selecione uma vendedora —</SelectItem>
                  {vendedoras.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.codigo ? `${v.codigo} · ` : ""}{v.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          {visao === "colunas_geral" && (
            <span className="text-xs text-blue-700 italic">
              📊 Visão em colunas com <b>todos os municípios</b> de todas as vendedoras agregados por categoria
            </span>
          )}
          {visao === "colunas_vendedora" && filtro.vendedora_id && (
            <span className="text-xs text-blue-700 italic">
              📋 Visão em colunas (Clientes · Consumidores · Não Clientes) da vendedora selecionada
            </span>
          )}
        </div>
      </Card>

      {/* Visão colunas geral: agrega TODOS os municípios (todas vendedoras) */}
      {visao === "colunas_geral" && (
        <CarteiraVendedora
          headerExtra={
            <div className="rounded-xl border border-blue-300 bg-blue-50 px-3 py-2 flex items-center gap-2" data-testid="banner-diretoria-colunas-geral">
              <span className="text-[10px] uppercase font-bold tracking-widest text-blue-700">Visão geral</span>
              <span className="font-bold text-slate-900">Todos os municípios · todas as vendedoras</span>
            </div>
          }
        />
      )}

      {/* Visão por vendedora: mesmo layout da vendedora, mas com badge do nome */}
      {visao === "colunas_vendedora" && filtro.vendedora_id && (
        <CarteiraVendedora
          vendedoraIdOverride={filtro.vendedora_id}
          headerExtra={
            <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 flex items-center gap-2" data-testid="banner-diretoria-vendo-vendedora">
              <span className="text-[10px] uppercase font-bold tracking-widest text-amber-700">Vendo carteira de:</span>
              <span className="font-bold text-slate-900">
                {(() => {
                  const v = vendedoras.find((x) => x.id === filtro.vendedora_id);
                  return v ? (v.codigo ? `${v.codigo} · ${v.nome}` : v.nome) : "?";
                })()}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setVisao("tabela"); setFiltro({ ...filtro, vendedora_id: "" }); }}
                className="ml-auto text-slate-600"
                data-testid="btn-voltar-tabela"
              >
                ← Voltar para tabela geral
              </Button>
            </div>
          }
        />
      )}

      {/* Barra de ações — só na visão tabela */}
      {visao === "tabela" && (
        <>
      <div className="flex items-center gap-2 flex-wrap">
        <Button onClick={baixarTemplate} variant="outline" data-testid="btn-baixar-template">
          <Download className="w-4 h-4 mr-1" /> Baixar template
        </Button>
        <Button onClick={() => setUploadOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-importar-carteira">
          <Upload className="w-4 h-4 mr-1" /> Importar Excel
        </Button>
        <Button onClick={() => setPrioMesOpen(true)} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="btn-definir-prio">
          <Star className="w-4 h-4 mr-1" /> Prioridades do mês
        </Button>
        <Button onClick={() => setNovoOrgaoOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-novo-orgao">
          <Plus className="w-4 h-4 mr-1" /> Novo órgão
        </Button>
        <NovoOrgaoModal open={novoOrgaoOpen} onClose={() => setNovoOrgaoOpen(false)}
          onCreated={(m) => { setNovoOrgaoOpen(false); nav(`/municipio/${m.id}`); }} />
        {user?.role === "diretoria" && <CeapIA onChanged={load} />}
        {user?.role === "diretoria" && (
          <Button onClick={() => setZerarOpen(true)} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50 ml-auto" data-testid="btn-zerar-funis">
            <AlertTriangle className="w-4 h-4 mr-1" /> Zerar todos os funis
          </Button>
        )}
      </div>

      {/* Filtros */}
      <Card className="p-4 rounded-2xl">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 text-sm font-bold text-slate-700 mr-2">
            <Filter className="w-4 h-4" /> Filtros
          </div>
          <Input placeholder="Buscar município..." value={filtro.search} onChange={(e) => setFiltro({ ...filtro, search: e.target.value })} className="w-48" data-testid="filtro-search" />
          <Select value={filtro.vendedora_id || "TODOS"} onValueChange={(v) => setFiltro({ ...filtro, vendedora_id: v === "TODOS" ? "" : v })}>
            <SelectTrigger className="w-48" data-testid="filtro-vendedora"><SelectValue placeholder="Vendedora" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="TODOS">Todas as vendedoras</SelectItem>
              <SelectItem value="SEM">Sem vendedora</SelectItem>
              {vendedoras.map((v) => (
                <SelectItem key={v.id} value={v.id}>
                  {v.codigo ? `${v.codigo} · ` : ""}{v.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filtro.categoria || "TODAS"} onValueChange={(v) => setFiltro({ ...filtro, categoria: v === "TODAS" ? "" : v })}>
            <SelectTrigger className="w-40" data-testid="filtro-categoria"><SelectValue placeholder="Categoria" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="TODAS">Todas categorias</SelectItem>
              <SelectItem value="cliente">Cliente</SelectItem>
              <SelectItem value="consumidor">Consumidor</SelectItem>
              <SelectItem value="nao_cliente">Não cliente</SelectItem>
              <SelectItem value="blacklist">Blacklist</SelectItem>
            </SelectContent>
          </Select>
          <Input placeholder="UF" value={filtro.uf} onChange={(e) => setFiltro({ ...filtro, uf: e.target.value.toUpperCase() })} className="w-16" maxLength={2} data-testid="filtro-uf" />
        </div>
      </Card>

      {/* Ações em lote */}
      {selected.size > 0 && (
        <Card className="p-3 rounded-2xl bg-blue-50 border-2 border-blue-200 sticky top-16 z-10">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-bold text-blue-800">{selected.size} selecionado(s)</div>
            <Select onValueChange={(v) => transferir(v === "NENHUMA" ? null : v)}>
              <SelectTrigger className="w-56 bg-white" data-testid="select-transferir"><SelectValue placeholder="Transferir para..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="NENHUMA">Remover vendedora</SelectItem>
                {vendedoras.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.codigo ? `${v.codigo} · ` : ""}{v.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select onValueChange={mudarCategoria}>
              <SelectTrigger className="w-56 bg-white" data-testid="select-categoria"><SelectValue placeholder="Mudar categoria para..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cliente">Cliente</SelectItem>
                <SelectItem value="consumidor">Consumidor</SelectItem>
                <SelectItem value="nao_cliente">Não cliente</SelectItem>
                <SelectItem value="blacklist">Blacklist</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())} className="ml-auto">
              <X className="w-4 h-4" /> Limpar
            </Button>
          </div>
        </Card>
      )}

      {/* Prioridades atuais — Iter 67 · colapsável; Diretor abre só ao clicar */}
      {prioridades.length > 0 && (
        <Card className="p-4 rounded-2xl border-2 border-amber-300 bg-amber-50/40">
          <button
            type="button"
            onClick={() => setPrioridadesExpanded((v) => !v)}
            className="w-full flex items-center gap-2 text-left hover:bg-amber-100/40 -mx-2 px-2 py-1 rounded transition-colors"
            data-testid="prioridades-toggle"
            aria-expanded={prioridadesExpanded}
          >
            <Star className="w-4 h-4 text-amber-600 fill-amber-400 shrink-0" />
            <h3 className="font-bold text-amber-900 flex-1">
              Prioridades do mês atual · {prioridades.length}
            </h3>
            {prioridadesExpanded ? (
              <ChevronUp className="w-4 h-4 text-amber-700" />
            ) : (
              <ChevronDown className="w-4 h-4 text-amber-700" />
            )}
          </button>
          {prioridadesExpanded && (
            <div className="flex flex-wrap gap-2 mt-3" data-testid="prioridades-lista">
              {prioridades.map((p) => {
                const m = muns.find((x) => x.id === p.municipio_id);
                return (
                  <div key={p.id} className="bg-white rounded-lg border border-amber-200 px-2 py-1 text-sm flex items-center gap-2">
                    <span className="font-medium">{m?.nome || p.municipio_id}</span>
                    {m?.uf && <span className="text-xs text-slate-500">{m.uf}</span>}
                    <button onClick={() => removerPrioridade(p.id)} className="text-red-500 hover:text-red-700" title="Remover">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {/* Tabela */}
      <Card className="p-0 rounded-2xl overflow-hidden">
        <div className="overflow-auto max-h-[70vh]">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 sticky top-0 z-10">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-600">
                <th className="p-2 w-8"><input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="check-all" /></th>
                <th className="p-2">Município</th>
                <th className="p-2">UF</th>
                <th className="p-2">Órgão</th>
                <th className="p-2">Categoria</th>
                <th className="p-2">Vendedora</th>
                <th className="p-2 text-right">Freq 12m</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {muns.map((m) => (
                <tr key={m.id} className="border-t hover:bg-slate-50" data-testid={`row-mun-${m.id}`}>
                  <td className="p-2"><input type="checkbox" checked={selected.has(m.id)} onChange={() => toggleSelect(m.id)} data-testid={`check-${m.id}`} /></td>
                  <td className="p-2 font-medium">
                    {prioIds.has(m.id) && <Star className="w-3 h-3 text-amber-500 fill-amber-400 inline mr-1" />}
                    {m.nome}
                  </td>
                  <td className="p-2">{m.uf}</td>
                  <td className="p-2">{m.tipo_orgao}</td>
                  <td className="p-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${CATEGORIAS_COR[m.categoria || "nao_cliente"]}`}>
                      {CATEGORIAS_LABEL[m.categoria || "nao_cliente"]}
                    </span>
                  </td>
                  <td className="p-2 text-slate-600">
                    {m.vendedora_nome ? (
                      <span>
                        {m.vendedora_codigo && <span className="text-xs bg-blue-50 text-blue-700 rounded px-1.5 py-0.5 font-bold mr-1">{m.vendedora_codigo}</span>}
                        {m.vendedora_nome}
                      </span>
                    ) : (
                      <span className="text-slate-400 italic">—</span>
                    )}
                  </td>
                  <td className="p-2 text-right tabular-nums">{m.frequencia_12m || 0}</td>
                  <td className="p-2 text-right">
                    <Button size="sm" variant="outline" onClick={() => nav(`/municipio/${m.id}`)} className="text-xs" data-testid={`btn-abrir-${m.id}`}>
                      Abrir
                    </Button>
                  </td>
                </tr>
              ))}
              {muns.length === 0 && (
                <tr><td colSpan={8} className="p-6 text-center text-slate-400">Nenhum município encontrado.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Modal Importar */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent data-testid="modal-importar">
          <DialogHeader><DialogTitle>Importar carteira via Excel</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-600 space-y-1">
              <p>1) Baixe o template — ele traz a aba <b>Vendedoras</b> com os códigos (V001, V002...).</p>
              <p>2) Na aba <b>Municípios</b>, preencha a coluna <b>vendedora_codigo</b> com o código da responsável (ex.: V001). Assim cada município vai automaticamente para a vendedora certa.</p>
              <p>3) Selecione o arquivo abaixo para importar.</p>
            </div>
            <Input type="file" accept=".xlsx,.xls" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} data-testid="input-file-import" />
            <div>
              <label className="text-xs font-bold uppercase text-slate-600">Ou atribuir uma única vendedora a TUDO (fallback opcional)</label>
              <Select value={uploadVendedora || "NENHUMA"} onValueChange={(v) => setUploadVendedora(v === "NENHUMA" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="— Usar código da planilha —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NENHUMA">— Usar código da planilha —</SelectItem>
                  {vendedoras.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.codigo ? `${v.codigo} · ` : ""}{v.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-slate-400 mt-1">
                Se você deixar em "usar código da planilha", cada linha usa a coluna vendedora_codigo. Se você escolher aqui, essa vendedora é aplicada apenas às linhas SEM código na planilha.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>Cancelar</Button>
            <Button onClick={importar} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-confirmar-import">Importar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Prioridades do mês */}
      <Dialog open={prioMesOpen} onOpenChange={setPrioMesOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="modal-prio-mes">
          <DialogHeader><DialogTitle>Prioridades do mês</DialogTitle></DialogHeader>
          <PrioridadesForm
            muns={muns}
            vendedoras={vendedoras}
            onSaved={() => { setPrioMesOpen(false); load(); }}
            onCancel={() => setPrioMesOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Modal Zerar Funis */}
      <Dialog open={zerarOpen} onOpenChange={setZerarOpen}>
        <DialogContent data-testid="modal-zerar">
          <DialogHeader><DialogTitle>Zerar todos os funis?</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-700 space-y-2">
            <p>Essa ação vai <b className="text-red-600">apagar TODOS os cards do funil</b> de todos os cursos. Inscrições e histórico de contatos permanecem preservados.</p>
            <p>Depois, as vendedoras vão prospectar pela Carteira e enviar novamente para os cursos.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setZerarOpen(false)}>Cancelar</Button>
            <Button onClick={zerarFunis} className="bg-red-600 hover:bg-red-700" data-testid="btn-confirmar-zerar">Sim, zerar tudo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </>
      )}
    </div>
  );
}

/* =====================================================
   Sub-componente: Formulário de Prioridades do Mês
   ===================================================== */
function PrioridadesForm({ muns, vendedoras, onSaved, onCancel }) {
  const [filtro, setFiltro] = useState({ vendedora_id: "", categoria: "", uf: "", search: "" });
  const [selecionados, setSelecionados] = useState([]);
  const [estrategia, setEstrategia] = useState("");
  const [busy, setBusy] = useState(false);

  const filtrados = muns.filter((m) => {
    if (filtro.vendedora_id === "SEM" && m.vendedora_id) return false;
    if (filtro.vendedora_id && filtro.vendedora_id !== "SEM" && m.vendedora_id !== filtro.vendedora_id) return false;
    if (filtro.categoria && (m.categoria || "nao_cliente") !== filtro.categoria) return false;
    if (filtro.uf && (m.uf || "").toUpperCase() !== filtro.uf.toUpperCase()) return false;
    if (filtro.search && !m.nome.toLowerCase().includes(filtro.search.toLowerCase())) return false;
    return true;
  });

  const toggleAll = () => {
    const ids = filtrados.map((m) => m.id);
    if (ids.every((id) => selecionados.includes(id))) {
      setSelecionados((s) => s.filter((x) => !ids.includes(x)));
    } else {
      setSelecionados((s) => Array.from(new Set([...s, ...ids])));
    }
  };

  const salvar = async () => {
    if (selecionados.length === 0) { toast.error("Selecione ao menos um município"); return; }
    setBusy(true);
    const mes_ano = new Date().toISOString().slice(0, 7);
    try {
      for (const mid of selecionados) {
        await api.post("/prioridades", { municipio_id: mid, mes_ano, estrategia: estrategia.trim() || null });
      }
      toast.success(`${selecionados.length} prioridade(s) definida(s)`);
      onSaved?.();
    } catch { toast.error("Erro ao marcar prioridades"); } finally { setBusy(false); }
  };

  const allChecked = filtrados.length > 0 && filtrados.every((m) => selecionados.includes(m.id));

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="text-xs text-slate-600 mb-2">
        Use os filtros para focar num grupo (ex.: vendedora Juliana + categoria Cliente + UF SC) e defina a estratégia.
      </div>

      {/* Filtros */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
        <Select value={filtro.vendedora_id || "TODAS"} onValueChange={(v) => setFiltro({ ...filtro, vendedora_id: v === "TODAS" ? "" : v })}>
          <SelectTrigger data-testid="prio-filtro-vend"><SelectValue placeholder="Vendedora" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="TODAS">Todas vendedoras</SelectItem>
            <SelectItem value="SEM">Sem vendedora</SelectItem>
            {vendedoras.map((v) => (
              <SelectItem key={v.id} value={v.id}>{v.codigo ? `${v.codigo} · ` : ""}{v.nome}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filtro.categoria || "TODAS"} onValueChange={(v) => setFiltro({ ...filtro, categoria: v === "TODAS" ? "" : v })}>
          <SelectTrigger data-testid="prio-filtro-cat"><SelectValue placeholder="Categoria" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="TODAS">Todas categorias</SelectItem>
            <SelectItem value="cliente">Cliente</SelectItem>
            <SelectItem value="consumidor">Consumidor</SelectItem>
            <SelectItem value="nao_cliente">Não cliente</SelectItem>
            <SelectItem value="blacklist">Blacklist</SelectItem>
          </SelectContent>
        </Select>
        <Input placeholder="UF" maxLength={2} value={filtro.uf} onChange={(e) => setFiltro({ ...filtro, uf: e.target.value.toUpperCase() })} data-testid="prio-filtro-uf" />
        <Input placeholder="Buscar município..." value={filtro.search} onChange={(e) => setFiltro({ ...filtro, search: e.target.value })} data-testid="prio-filtro-search" />
      </div>

      {/* Barra de status */}
      <div className="flex items-center justify-between px-1 py-1 text-xs text-slate-600 bg-slate-50 rounded mb-1">
        <span>{filtrados.length} município(s) filtrado(s) · <b className="text-amber-700">{selecionados.length} selecionado(s)</b></span>
        <button onClick={toggleAll} className="text-blue-600 hover:underline font-semibold" data-testid="btn-toggle-all-prio">
          {allChecked ? "Desmarcar todos" : "Selecionar todos filtrados"}
        </button>
      </div>

      {/* Lista */}
      <div className="flex-1 overflow-auto border rounded-lg p-1 min-h-[200px]" style={{ maxHeight: "40vh" }}>
        {filtrados.length === 0 ? (
          <div className="text-slate-400 text-sm py-6 text-center">Nenhum município com esses filtros.</div>
        ) : filtrados.map((m) => (
          <label key={m.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 rounded cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={selecionados.includes(m.id)}
              onChange={(e) => setSelecionados((t) => e.target.checked ? [...t, m.id] : t.filter((x) => x !== m.id))}
              data-testid={`prio-check-${m.id}`}
            />
            <span className="font-medium flex-1">{m.nome}</span>
            <span className="text-xs bg-slate-100 rounded px-1.5 py-0.5 text-slate-700">{m.uf}</span>
            <span className="text-xs bg-blue-50 rounded px-1.5 py-0.5 text-blue-700">{m.tipo_orgao || "Câmara"}</span>
            <span className={`text-xs rounded px-1.5 py-0.5 ${CATEGORIAS_COR[m.categoria || "nao_cliente"]}`}>
              {CATEGORIAS_LABEL[m.categoria || "nao_cliente"]}
            </span>
            {m.vendedora_nome && (
              <span className="text-xs text-slate-500 min-w-[80px] text-right truncate">
                {m.vendedora_codigo && <span className="font-bold text-blue-600 mr-1">{m.vendedora_codigo}</span>}
                {m.vendedora_nome}
              </span>
            )}
          </label>
        ))}
      </div>

      {/* Estratégia */}
      <div className="mt-3">
        <label className="text-xs font-bold uppercase text-slate-600">Estratégia (aplicada às marcações)</label>
        <Textarea
          placeholder="Ex.: Reativar clientes SC que compraram no 1º semestre com abordagem consultiva focando no curso Y..."
          value={estrategia}
          onChange={(e) => setEstrategia(e.target.value)}
          rows={2}
          data-testid="prio-estrategia"
        />
      </div>

      <div className="flex justify-end gap-2 mt-3 pt-3 border-t">
        <Button variant="outline" onClick={onCancel} disabled={busy}>Cancelar</Button>
        <Button onClick={salvar} disabled={busy || selecionados.length === 0} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="btn-salvar-prio">
          <Star className="w-4 h-4 mr-1" /> {busy ? "Salvando..." : `Salvar ${selecionados.length} prioridade(s)`}
        </Button>
      </div>
    </div>
  );
}

/* =====================================================
   Página principal
   ===================================================== */
export default function Carteira() {
  const { user } = useAuth();
  return (
    <div className="space-y-6" data-testid="carteira-page">
      <div className="flex items-center gap-2">
        <Wallet className="w-6 h-6 text-blue-600" />
        <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900">Carteira</h1>
        <span className="text-xs text-slate-500 ml-2">
          {user?.role === "vendedora" ? "Sua carteira, organizada por categoria" : "Gestão de carteiras e prioridades"}
        </span>
      </div>
      {user?.role === "vendedora" ? <CarteiraVendedora /> : <CarteiraDiretoria />}
    </div>
  );
}
